import React, { useEffect, useRef, useState } from 'react';
import { Form, Select, Button, Row, Col, Popconfirm } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../../../config';

// initialAdmissions: mảng admissions từ API (đã có subject_type_id), dùng để khởi tạo filter
const AdmissionsList = ({ subjects, form, initialAdmissions = [] }) => {
    const [subjectTypes, setSubjectTypes] = useState([]);
    const [filteredSubjectsByIndex, setFilteredSubjectsByIndex] = useState({});

    // Khi subjects và initialAdmissions đều có dữ liệu, khởi tạo filtered list cho từng row
    // Dùng initialAdmissions (prop từ API) thay vì form.getFieldValue để tránh vấn đề timing
    useEffect(() => {
        if (!subjects?.length || !initialAdmissions?.length) return;

        const initial = {};
        initialAdmissions.forEach((admission, index) => {
            if (admission?.subject_type_id) {
                initial[index] = subjects.filter(s => s.subject_type_id === admission.subject_type_id);
            }
        });

        if (Object.keys(initial).length > 0) {
            setFilteredSubjectsByIndex(initial);
        }
    }, [subjects, initialAdmissions]);

    useEffect(() => {
        const fetchSubjectTypes = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${config.apiUrl}/api/v1/subject-types`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });
                if (response.data.success) {
                    setSubjectTypes(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching subject types:', error);
            }
        };
        fetchSubjectTypes();
    }, []);

    // Force subject_type_id fields to re-map ID -> label after subjectTypes loaded (edit mode)
    // subjectTypes được fetch riêng trong component này nên có thể tải xong SAU khi
    // form đã setFieldsValue từ profile, khiến Select hiển thị trống dù value đã có.
    // Match theo `id` (không theo index) để không bị lệch nếu user đã add/remove nguyện vọng
    // trước khi subjectTypes tải xong; chỉ áp dụng một lần để không đè lên thay đổi sau đó của user.
    const appliedInitialTypeFixRef = useRef(false);
    useEffect(() => {
        if (appliedInitialTypeFixRef.current) return;
        if (!subjectTypes?.length || !initialAdmissions?.length) return;

        const initialTypeById = new Map(
            initialAdmissions
                .filter(a => a?.id != null && a?.subject_type_id)
                .map(a => [a.id, a.subject_type_id])
        );
        if (initialTypeById.size === 0) return;

        const currentAdmissions = form.getFieldValue('admissions') || [];
        const updated = currentAdmissions.map(admission => {
            const typeId = admission?.id != null ? initialTypeById.get(admission.id) : undefined;
            return typeId ? { ...admission, subject_type_id: typeId } : admission;
        });
        form.setFieldValue('admissions', updated);
        appliedInitialTypeFixRef.current = true;
    }, [subjectTypes, initialAdmissions, form]);

    const handleTypeChange = (value, index, name) => {
        const filtered = value
            ? subjects.filter(s => s.subject_type_id === value)
            : subjects;
        setFilteredSubjectsByIndex(prev => ({ ...prev, [index]: filtered }));

        // Xóa ngành đã chọn khi đổi loại ngành
        const admissions = form.getFieldValue('admissions') || [];
        if (admissions[name] !== undefined) {
            const updated = [...admissions];
            updated[name] = { ...updated[name], subject_id: undefined };
            form.setFieldValue('admissions', updated);
        }
    };

    return (
        <Form.List name="admissions">
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }, index) => (
                        <React.Fragment key={key}>
                            <span style={{ fontSize: 16, fontWeight: 'bold' }}>Nguyện vọng {index + 2}</span>
                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={24} sm={10}>
                                    <Form.Item
                                        {...restField}
                                        label="Khối ngành dự tuyển"
                                        name={[name, 'subject_type_id']}
                                    >
                                        <Select
                                            placeholder="Chọn Khối ngành dự tuyển"
                                            onChange={(value) => handleTypeChange(value, index, name)}
                                            options={subjectTypes.map(type => ({
                                                value: type.id,
                                                label: type.name
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        {...restField}
                                        label="Ngành/chuyên ngành"
                                        name={[name, 'subject_id']}
                                        dependencies={[[name, 'subject_type_id']]}
                                    >
                                        <Select
                                            showSearch
                                            placeholder="Ngành/chuyên ngành"
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={(filteredSubjectsByIndex[index] ?? subjects).map(s => ({
                                                value: s.id,
                                                label: `${s.name} - ${s.code}`
                                            }))}
                                            disabled={!form.getFieldValue(['admissions', name, 'subject_type_id'])}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={2} style={{ textAlign: 'right' }}>
                                    <Popconfirm
                                        title="Xác nhận xóa"
                                        description="Bạn có chắc chắn muốn xóa nguyện vọng này?"
                                        onConfirm={() => remove(name)}
                                        okText="Xóa"
                                        cancelText="Hủy"
                                    >
                                        <Button type="primary" danger>Xóa</Button>
                                    </Popconfirm>
                                </Col>
                            </Row>
                        </React.Fragment>
                    ))}
                    {fields.length < 4 && (
                        <Button
                            type="primary"
                            onClick={add}
                            icon={<PlusOutlined />}
                        >
                            Thêm nguyện vọng
                        </Button>
                    )}
                </>
            )}
        </Form.List>
    );
};

export default React.memo(AdmissionsList);
